import config from "@config";
import actionCreators from "@modules/hierarchy/actionCreator";
import {
  AddBranch,
  DeleteBranch,
  DiscardDraft,
  ExportActiveHierarchy,
  ExportActiveHierarchySuccess,
  CommitDraftHierarchy,
  CreateDraftHierarchy,
  GetActiveHierarchyTree,
  GetActiveHierarchyTreeCsv,
  GetDraftHierarchyTree,
  GetDraftHierarchyTreeChangelog,
  GetDraftHierarchyTreeCsv,
  MoveBranch,
  RenameBranch,
  RevertChange,
  DropBranch,
  SubmitDraftHierarchyTree,
  ViewScopeChanges,
  ToggleAddDepartmentModal,
  ToggleMoveDepartmentModal,
  ToggleRenameDepartmentModal,
  ToggleDeleteDepartmentModal,
  SelectOrganizationalUnit,
  CreateHierarchyNotification,
  UpdateHierarchyNotification,
  GetNotificationContacts,
  GetHierarchyShiftPatterns,
  GetHierarchyNotificationsSummary,
  GetHierarchyShiftPatternSchema,
  CreateHierarchyShiftPattern,
  UpdateHierarchyShiftPattern,
  DeleteHierarchyNotification,
  DeleteHierarchyShiftPatternAssignment,
  CutDepartment,
  CallbacksEmployeeSearch,
} from "@modules/hierarchy/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { all, call, put, takeEvery, race, take, spawn, select } from "redux-saga/effects";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";
import { transformFilters, OPERATORS } from "@transformers/request";
import http from "@http";
import hierarchyChangelogSagas from "./change-log";
import departmentContactsSagas from "./department-contacts";
import hierarcchyChangesSagas from "./hierarchy-changes";
import {
  getselectedOrganisationalUnit,
  getDraftHierarchyTree,
  getAssignedShiftPattern,
} from "../selectors";

type DeleteHierarchyNotificationRequest = $Exact<{
  clientId: number,
  organisationalUnitId: number,
  notificationIds: number[],
  successMessage: string,
}>;

export function* onDeleteHierarchyNotification({
  payload,
}: Action<DeleteHierarchyNotificationRequest>): Generator<*, void, void> {
  const clientId = payload.clientId || null;
  const organisationalUnitId = payload.organisationalUnitId || null;
  const notificationIds = payload.notificationIds || null;
  const successMessage = payload.successMessage || "SUCCESSFULLY_DELETED";

  try {
    if (!payload || !clientId || !organisationalUnitId || !notificationIds) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const branch = yield select(getselectedOrganisationalUnit);
    yield all(
      notificationIds.map(notificationId =>
        call(
          http.delete,
          `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${organisationalUnitId}/notification/${notificationId}`,
        ),
      ),
    );

    yield all([
      put(actionCreators.DeleteHierarchyNotificationSuccess()),
      put(sendSuccessMessage(successMessage)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteHierarchyNotificationError(e.response));
  }
}

type DeleteHierarchyShiftPatternAssignmentRequest = $Exact<{
  clientId: number,
  organisationalUnitId: number,
  data: Array<
    $Exact<{
      assignmentRuleId: number,
      hierarchyUnassignShiftPatternDTO: $Exact<{
        isCascading: boolean,
      }>,
    }>,
  >,
}>;

export function* onDeleteHierarchyShiftPatternAssignment({
  payload,
}: Action<DeleteHierarchyShiftPatternAssignmentRequest>): Generator<*, void, void> {
  const clientId = payload.clientId || null;
  const organisationalUnitId = payload.organisationalUnitId || null;
  const data = payload.data || null;
  const successMessage = payload.successMessage || "SUCCESSFULLY_DELETED";

  try {
    if (!payload || !clientId || !organisationalUnitId || !data) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const branch = yield select(getselectedOrganisationalUnit);
    yield all(
      data.map(item =>
        call(
          http.delete,
          `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${organisationalUnitId}/shift-pattern/assignment/${item.assignmentRuleId}`,
          { data: item.hierarchyUnassignShiftPatternDTO },
        ),
      ),
    );

    yield all([
      put(actionCreators.DeleteHierarchyShiftPatternAssignmentSuccess()),
      put(sendSuccessMessage(successMessage)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteHierarchyShiftPatternAssignmentError(e.response));
  }
}

export function* onDiscardDraft({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(http.delete, `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft`);

    yield all([
      yield put(actionCreators.DiscardDraftSuccess(null)),
      yield put(sendSuccessMessage("DRAFT_DISCARDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardDraftError(e.response));
  }
}

export function* onToggleAddDepartmentModal({ payload }) {
  const clientId = payload.clientId;
  const branch = payload.branch;
  const visible = payload.visible;

  // If we are closing the modal skip
  if (!visible) {
    return;
  }

  try {
    const { formData } = yield race({
      formData: take(AddBranch),
      cancel: take(ToggleAddDepartmentModal),
    });

    if (formData) {
      const response = yield call(
        http.post,
        `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft/organisational-unit/${branch.data.id}/add`,
        formData.payload,
      );

      yield all([
        put(actionCreators.AddBranchSuccess(response.data)),
        put(actionCreators.GetDraftHierarchyTree({ clientId })),
        put(actionCreators.ToggleAddDepartmentModal({ visible: false })),
        put(sendSuccessMessage("DEPARTMENT_ADDED")),
      ]);
    }
  } catch (e) {
    yield all([
      put(actionCreators.AddBranchError(e.response)),
      put(actionCreators.ToggleAddDepartmentModal({ visible: false })),
    ]);
  }
}

export function* onCommitDraftHierarchy({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const clientId = payload.clientId;
    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/commit`,
    );

    yield all([
      put(actionCreators.CommitDraftHierarchySuccess(response.data)),
      put(actionCreators.GetActiveHierarchyTree({ clientId })),
      put(actionCreators.GetDraftHierarchyTreeSuccess(null)),
      put(actionCreators.GetDraftHierarchyTreeChangelogSuccess(null)),
      put(actionCreators.ToggleEditDraftHierarchyModal(false)),
      put(actionCreators.GetHierarchyChangelog({ clientId, page: 1 })),
      put(sendSuccessMessage("HIERARCHY_DRAFT_PUBLISH_SUCCESS")),
    ]);
  } catch (e) {
    yield put(actionCreators.CommitDraftHierarchyError(e.response));
  }
}

export function* onToggleMoveDepartmentModal({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.branch) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    // If we are closing the modal skip
    if (!payload.visible) {
      return;
    }

    const { clientId, branch } = payload;
    const { formData } = yield race({
      formData: take(MoveBranch),
      cancel: take(ToggleMoveDepartmentModal),
    });

    if (formData) {
      const response = yield call(
        http.post,
        `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/organisational-unit/${branch.data.id}/move`,
        formData.payload,
      );

      yield all([
        put(actionCreators.MoveBranchSuccess(response.data)),
        put(actionCreators.GetDraftHierarchyTree({ clientId })),
        put(actionCreators.ToggleMoveDepartmentModal({ visible: false })),
        put(sendSuccessMessage("DEPARTMENT_MOVED")),
      ]);
    }
  } catch (e) {
    yield put(actionCreators.MoveBranchError(e.response));
  }
}

export function* onDropBranch({ payload }) {
  try {
    if (!payload || !payload.from || !payload.to) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const clientId = payload.clientId;
    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/organisational-unit/${payload.from}/move`,
      {
        newParentId: payload.to,
      },
    );

    yield all([
      put(actionCreators.DropBranchSuccess(response.data)),
      put(actionCreators.GetDraftHierarchyTree({ clientId })),
      put(sendSuccessMessage("DEPARTMENT_MOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DropBranchError(e.response));
  }
}

export function* onToggleRenameDepartmentModal({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.branch) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    // If we are closing the modal skip
    if (!payload.visible) {
      return;
    }

    const { branch, clientId } = payload;
    const { formData } = yield race({
      formData: take(RenameBranch),
      cancel: take(ToggleRenameDepartmentModal),
    });

    if (formData) {
      const response = yield call(
        http.post,
        `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/organisational-unit/${branch.data.id}/rename`,
        formData.payload,
      );

      yield all([
        put(actionCreators.RenameBranchSuccess(response.data)),
        put(actionCreators.GetDraftHierarchyTree({ clientId })),
        put(actionCreators.ToggleRenameDepartmentModal({ visible: false })),
        put(sendSuccessMessage("DEPARTMENT_RENAMED")),
      ]);
    }
  } catch (e) {
    yield put(actionCreators.RenameBranchError(e.response));
  }
}

export function* onToggleDeleteDepartmentModal({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.branch) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    // If we are closing the modal skip
    if (!payload.visible) {
      return;
    }

    const { branch, clientId } = payload;
    const { formData } = yield race({
      formData: take(DeleteBranch),
      cancel: take(ToggleDeleteDepartmentModal),
    });

    if (formData) {
      const response = yield call(
        http.delete,
        `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/organisational-unit/${branch.data.id}`,
      );

      yield all([
        put(actionCreators.DeleteBranchSuccess(response.data)),
        put(actionCreators.GetDraftHierarchyTree({ clientId })),
        put(actionCreators.ToggleDeleteDepartmentModal({ visible: false })),
        put(actionCreators.ToggleDeleteDepartmentModalWarning({ visible: false, text: null })),
        put(sendSuccessMessage("DEPARTMENT_DELETED")),
      ]);
    }
  } catch (e) {
    yield put(actionCreators.DeleteBranchError(e.response));
  }
}

export function* onGetActiveHierarchyTree({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/active`,
      {
        params: {
          includeEmployeePositions: true,
        },
      },
    );

    yield put(actionCreators.getActiveHierarchyTreeSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getActiveHierarchyTreeError(e.response));
  }
}

export function* onCreateDraftHierarchy({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft`,
    );

    yield all([
      yield put(actionCreators.CreateDraftHierarchySuccess(response.data)),
      yield put(actionCreators.GetDraftHierarchyTreeChangelog({ clientId: payload.clientId })),
      yield put(actionCreators.ToggleEditDraftHierarchyModal(true)),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateDraftHierarchyError(e.response));
  }
}

export function* onGetActiveHierarchyTreeCsv({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/active/export`,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield downloadCsvFile(response.data, "active-hierarchy.csv");
  } catch (e) {
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onGetDraftHierarchyTree({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft`,
      {
        params: {
          includeEmployeePositions: true,
        },
      },
    );

    yield put(actionCreators.GetDraftHierarchyTreeSuccess(response.data));
    yield put(actionCreators.GetDraftHierarchyTreeChangelog({ clientId: payload.clientId }));
  } catch (e) {
    // 404 means no draft, don't show errors
    if (e.response.status !== 404) {
      yield put(actionCreators.GetDraftHierarchyTreeError(e.response));
    } else {
      yield put(actionCreators.GetDraftHierarchyTreeSuccess(null));
    }
  }
}

export function* onGetDraftHierarchyTreeChangelog({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const page = payload.page || 1;
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft/log`,
      {
        params: {
          page,
          sort: "asc",
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetDraftHierarchyTreeChangelogSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDraftHierarchyTreeChangelogError(e.response));
  }
}

export function* getNotificationContacts({ payload }) {
  try {
    if (!payload || !payload.branch || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const clientId = payload.clientId;
    const branch = payload.branch;
    const page = payload.page || 1;

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${branch.data.id}/notification`,
      {
        params: {
          includeInherited: true,
          page,
        },
      },
    );

    yield put(actionCreators.GetHierarchyNotificationContactsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNotificationContactsError(e.response));
  }
}

export function* onCreateHierarchyNotification({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const branch = yield select(getselectedOrganisationalUnit);
    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/notification`,
      payload.data,
    );
    yield all([
      put(actionCreators.CreateHierarchyNotificationSuccess()),
      put(sendSuccessMessage("CONTACT_ADDED")),
      put(actionCreators.ToggleAddEditContactModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateHierarchyNotificationError(e.response));
  }
}

type UpdateHierarchyNotificationRequest = $Exact<{
  clientId: number,
  organisationalUnitId: number,
  data: $Exact<{
    id: number,
    isCascading: boolean,
    notificationType: string,
    organisationalUnit: $Exact<{ id: number }>,
    recipient: $Exact<{ id: number }>,
  }>,
}>;

export function* onUpdateHierarchyNotification({
  payload,
}: Action<UpdateHierarchyNotificationRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const branch = yield select(getselectedOrganisationalUnit);
    const response = yield call(
      http.put,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/notification/${payload.data.id}`,
      payload.data,
    );

    yield all([
      put(actionCreators.updateHierarchyNotificationSuccess(response.data)),
      put(sendSuccessMessage("CONTACT_UPDATED")),
      put(actionCreators.ToggleAddEditContactModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateHierarchyNotificationError(e.response));
  }
}

export function* onCreateHierarchyShiftPattern({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const branch = yield select(getselectedOrganisationalUnit);
    yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/shift-pattern`,
      payload.data,
    );
    yield all([
      put(actionCreators.CreateHierarchyShiftPatternSuccess()),
      put(sendSuccessMessage("SHIFT_PATTERN_ADDED")),
      put(actionCreators.ToggleAddEditShiftPatternModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateHierarchyShiftPatternError(e.response));
  }
}

type UpdateHierarchyShiftPatternRequest = $Exact<{
  clientId: number,
  organisationalUnitId: number,
  data: Object,
}>;

export function* onUpdateHierarchyShiftPattern({
  payload,
}: Action<UpdateHierarchyShiftPatternRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const branch = yield select(getselectedOrganisationalUnit);
    const hierarchyShiftPattern = yield select(getAssignedShiftPattern);

    yield call(
      http.delete,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/shift-pattern/assignment/${payload.data.id}`,
      { data: { isCascading: hierarchyShiftPattern.isCascading } },
    );

    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/shift-pattern`,
      payload.data,
    );

    yield all([
      put(actionCreators.updateHierarchyShiftPatternSuccess(response.data)),
      put(sendSuccessMessage("SHIFT_PATTERN_UPDATED")),
      put(actionCreators.ToggleAddEditShiftPatternModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateHierarchyShiftPatternError(e.response));
  }
}

type HierarchyShiftPatternSchemaRequest = $Exact<{
  clientId: string,
}>;

export function* onGetHierarchyShiftPatternSchema({
  payload,
}: Action<HierarchyShiftPatternSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const { clientId } = payload;
    const schema = yield parseSchema("HierarchyShiftPatternDTO", { clientId });
    yield put(actionCreators.GetHierarchyShiftPatternSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetHierarchyShiftPatternSchemaError(e.response));
  }
}

export function* onGetHierarchyNotificationsSummary({ payload }) {
  const clientId = payload.clientId || null;
  const branch = payload.branch || null;

  try {
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${branch.data.id}/notification/summary`,
    );

    yield put(actionCreators.GetHierarchyNotificationsSummarySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetHierarchyNotificationsSummaryError(e.response));
  }
}

export function* getShiftPatterns({ payload }) {
  const clientId = payload.clientId || null;
  const branch = payload.branch || null;
  const page = payload.page || 1;

  try {
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${branch.data.id}/shift-pattern`,
      {
        params: {
          page,
        },
      },
    );

    yield put(actionCreators.GetHierarchyShiftPatternsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetHierarchyShiftPatternsError(e.response));
  }
}

export function* onSelectOrganizationalUnit({ payload }) {
  yield all([
    put(actionCreators.GetNotificationContacts(payload)),
    put(actionCreators.GetHierarchyShiftPatterns(payload)),
    put(actionCreators.GetHierarchyNotificationsSummary(payload)),
  ]);
}

export function* onExportActiveHierarchy({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/active/export`,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportActiveHierarchySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportActiveHierarchySuccessError(e.response));
  }
}

export function* onGetDraftHierarchyTreeCsv({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/export`,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield downloadCsvFile(response.data, "draft-hierarchy.csv");
  } catch (e) {
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onRevertChange({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.changeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft/revert/${payload.changeId}`,
    );

    yield all([
      put(actionCreators.RevertChangeSuccess(payload.data)),
      put(actionCreators.GetDraftHierarchyTree({ clientId: payload.clientId })),
      put(sendSuccessMessage("CHANGE_REVERTED")),
    ]);
  } catch (e) {
    yield put(actionCreators.RevertChangeError(e.response));
  }
}

export function* onSubmitDraftHierarchyTree({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/commit`,
    );
    yield put(actionCreators.getActiveHierarchyTreeSuccess(response.data));
    yield put(actionCreators.getDraftHierarchyTreeSuccess(null));
    yield put(actionCreators.setHierarchyModal(null));
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
    yield put(actionCreators.updateStep(0));
  } catch (e) {
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onViewScopeChanges({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/active`,
      {
        params: {},
      },
    );
    yield put(actionCreators.getDraftHierarchyTreeError(response.data));
  } catch (e) {
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onCutDepartment({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.changeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { branch, clientId } = payload;
    const tree = yield select(getDraftHierarchyTree);
    const uassignedNode = tree.structure.children.find(
      item => item.data.reference === "UNASSIGNED",
    );

    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/organisational-unit/${branch.data.id}/move`,
      {
        newParentId: uassignedNode.data.id,
      },
    );

    yield all([
      put(actionCreators.MoveBranchSuccess(payload.data)),
      put(actionCreators.GetDraftHierarchyTree({ clientId })),
      put(sendSuccessMessage("DEPARTMENT_CUT")),
    ]);
  } catch (e) {
    yield put(actionCreators.CutDepartmentError(e.response));
  }
}

export function* onExportActiveHierarchySuccess({
  payload,
}: {
  payload: string,
}): Generator<void, void, void> {
  yield downloadCsvFile(payload, "hierarchy.csv");
}

function* onCallbacksEmployeeSearch({ payload }: Action<Object>): Generator<void, void, Object> {
  if (!payload || !payload.data || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const page = payload && payload.page;

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee`,
      {
        params: {
          search: transformFilters(payload.data, { last_name: OPERATORS.STARTS_WITH }),
          page,
          limit: payload.limit || config.shortResultsPerPage,
        },
      },
    );

    yield put(actionCreators.CallbacksEmployeeSearchSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.CallbacksEmployeeSearchError(e.response));
  }
}

export function* HierarchySaga() {
  yield all([
    // Hierarchy Admin
    takeEvery(DropBranch, onDropBranch),
    takeEvery(ExportActiveHierarchy, onExportActiveHierarchy),
    takeEvery(ExportActiveHierarchySuccess, onExportActiveHierarchySuccess),
    takeEvery(ToggleAddDepartmentModal, onToggleAddDepartmentModal),
    takeEvery(ToggleMoveDepartmentModal, onToggleMoveDepartmentModal),
    takeEvery(ToggleRenameDepartmentModal, onToggleRenameDepartmentModal),
    takeEvery(ToggleDeleteDepartmentModal, onToggleDeleteDepartmentModal),
    takeEvery(DiscardDraft, onDiscardDraft),
    takeEvery(CommitDraftHierarchy, onCommitDraftHierarchy),
    takeEvery(CreateDraftHierarchy, onCreateDraftHierarchy),
    takeEvery(GetActiveHierarchyTree, onGetActiveHierarchyTree),
    takeEvery(GetActiveHierarchyTreeCsv, onGetActiveHierarchyTreeCsv),
    takeEvery(GetDraftHierarchyTree, onGetDraftHierarchyTree),
    takeEvery(GetDraftHierarchyTreeChangelog, onGetDraftHierarchyTreeChangelog),
    takeEvery(GetDraftHierarchyTreeCsv, onGetDraftHierarchyTreeCsv),
    takeEvery(RevertChange, onRevertChange),
    takeEvery(SubmitDraftHierarchyTree, onSubmitDraftHierarchyTree),
    takeEvery(ViewScopeChanges, onViewScopeChanges),
    takeEvery(SelectOrganizationalUnit, onSelectOrganizationalUnit),
    takeEvery(GetHierarchyShiftPatterns, getShiftPatterns),
    takeEvery(GetNotificationContacts, getNotificationContacts),
    takeEvery(GetHierarchyNotificationsSummary, onGetHierarchyNotificationsSummary),
    takeEvery(CreateHierarchyNotification, onCreateHierarchyNotification),
    takeEvery(UpdateHierarchyNotification, onUpdateHierarchyNotification),
    takeEvery(GetHierarchyShiftPatternSchema, onGetHierarchyShiftPatternSchema),
    takeEvery(CreateHierarchyShiftPattern, onCreateHierarchyShiftPattern),
    takeEvery(UpdateHierarchyShiftPattern, onUpdateHierarchyShiftPattern),
    takeEvery(DeleteHierarchyNotification, onDeleteHierarchyNotification),
    takeEvery(DeleteHierarchyShiftPatternAssignment, onDeleteHierarchyShiftPatternAssignment),
    takeEvery(CutDepartment, onCutDepartment),
    takeEvery(CallbacksEmployeeSearch, onCallbacksEmployeeSearch),
    spawn(hierarchyChangelogSagas),
    spawn(departmentContactsSagas),
    spawn(hierarcchyChangesSagas),
  ]);
}

export default HierarchySaga;
